import * as React from 'react'

import { NextImage } from '~shared/components/Images/NextImage'
import { Card } from '~ui/components/content/Card/Card'
import { Box, FlexCentered } from '~ui/components/layout/Box'
import { Skeleton } from '~ui/components/loadingSkeletons/Skeleton'
import { AspectRatio } from '~ui/components/media/AspectRatio/AspectRatio'
import { PictogramIcon } from '~ui/Flaticon/PictogramIcon'
import { getPictogramForLabel } from '~ui/Flaticon/pictograms'

import { useProductDetailLayout } from '../../config/productDetailLayoutSettings'

type TProps = NoChildren & {
  /**
   * if true, only a Skeleton (roughly in a size of pictogram Card) will be rendered
   */
  showPictogramPlaceholder?: boolean
  /**
   * loading indicator, if `true` a blur filter is applied
   */
  isLoading?: boolean
  /**
   * low resolution image that will be used with a blur filter while loading the image
   */
  placeholderUrl?: Nullable<PropsOf<typeof NextImage>['blurDataURL']>
  /**
   * image to render
   */
  imageUrl?: Nullable<PropsOf<typeof NextImage>['src']>
  /**
   * product pictogram
   */
  pictogram: Nullable<string>
}

const PICTOGRAM_DIMENSIONS = `10.25rem` // TODO make this synced with the real value
const FALLBACK_PLACEHOLDER =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk0NCoBwABUQDRWvwzmQAAAABJRU5ErkJggg=='

/**
 * Renders product image or pictogram or null, if both are unavailable.
 * While the image is loading, the blur effect is applied.
 */
export const DetailImage: React.FC<TProps> = ({
  imageUrl,
  isLoading,
  pictogram,
  placeholderUrl,
  showPictogramPlaceholder,
}) => {
  const { productDetailImageSize } = useProductDetailLayout()

  const radius = productDetailImageSize === `100%` ? `none` : `small`

  // TODO figure out better render flow (and add this skeleton around real pictogram)?
  if (showPictogramPlaceholder) {
    return (
      <Skeleton radius={radius}>
        <Box
          width={PICTOGRAM_DIMENSIONS}
          height={PICTOGRAM_DIMENSIONS}
          minWidth={PICTOGRAM_DIMENSIONS}
        />
      </Skeleton>
    )
  }

  if (placeholderUrl || imageUrl) {
    return (
      <Box
        radius={radius}
        overflow={`hidden`}
        position={`relative`}
        width={productDetailImageSize}
        filter={isLoading ? `blur(1rem)` : `unset`}
      >
        <AspectRatio maxWidth={`100%`}>
          <NextImage
            layout={`fill`}
            placeholder={`blur`}
            blurDataURL={placeholderUrl ?? FALLBACK_PLACEHOLDER}
            src={imageUrl ?? FALLBACK_PLACEHOLDER}
          />
        </AspectRatio>
      </Box>
    )
  }

  const pictogramData = getPictogramForLabel(pictogram)

  if (pictogramData) {
    return (
      <Card bgColor={`bgColors.card`} padding={`extraLarge`} radius={radius}>
        <FlexCentered>
          <PictogramIcon size={3} icon={pictogramData.icon} />
        </FlexCentered>
      </Card>
    )
  }

  // product has no image, placeholder or pictogram
  return null
}
